import CTNModal from '@ctn/ui/components/ctn-modal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import CTNTextArea from '@ctn/ui/components/ctn-textarea';
import { type CTNModalProps } from '@ctn/ui/types/ctn-modal';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useEffect, useState, type ReactElement } from 'react';
import { useForm, useFormContext, type Resolver } from 'react-hook-form';
import * as yup from 'yup';

import { useGlobalContext } from '@/context/global.context';
import { postCancelarOperacion } from '@/services/tramite.service';

import ERROR_MESSAGE from '@/constants/errors.constants';
import { notificationsConfig } from '@/helpers/notifications';
import { peekValidation } from '@/helpers/validations';
import { type FormTramiteValues } from '@/interfaces/model/don.interface';


type ModalParams = {
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly onSave?: () => void;
};

type Form = {
	motivo: string;
};

const schema = yup.object().shape({
	motivo: yup.string().required(ERROR_MESSAGE.required),
});

export const resolver = (): Resolver<any, string> => yupResolver(schema);

const ModalCancelacion = ({ isOpen, onClose, onSave }: ModalParams): ReactElement => {
	const { user } = useGlobalContext();
	const { getValues } = useFormContext<FormTramiteValues>();
	const [hasMissingRequired, setHasMissingRequired] = useState(true);
	const [isHidden, setIsHidden] = useState(false);
	const { openNotification, closeNotification } = useNotification();
	const configuracionTemplate = notificationsConfig({ closeNotification });
	const notificationTemplate = notificationsConfig({ closeNotification });
	const [loading, setLoading] = useState(false);

	const {
		control,
		formState: { errors },
		watch,
		reset,
		handleSubmit,
	} = useForm<Form>({
		mode: 'onSubmit',
		defaultValues: { motivo: '' },
		resolver: resolver(),
	});

	const formData = watch();

	const handleSave = async ({ motivo }: Form) => {
		if (!user) return;
		const [nifEmisor, idTramite, numeroTramite] = getValues(['nifEmisor', 'id', 'numeroTramite']);

		const payload = {
			motivo,
			nifEmisor,
			idTramite,
			numeroTramite,
			tipoSector: user?.tipoSectorEmisor,
		};
		setLoading(true);
		const response = await postCancelarOperacion(payload);
		setLoading(false);

		if (response.code === 406 && response.data?.errores) {
			setIsHidden(true);
			openNotification(
				configuracionTemplate.error406({
					message: 'Se ha producido un error en la cancelación:',
					errores: response.data?.errores,
					onClickAccept: () => setIsHidden(false)
				}),
			);
			return;
		}

		if (response.code === 200) {
			openNotification(
				notificationTemplate.successMessage(
					'Solicitud cancelada',
					'La solicitud ha sido cancelada correctamente.',
				),
			);

			if (onSave) {
				onSave();
			}

			onClose();
			return;
		}

		setIsHidden(true);
		openNotification(
			configuracionTemplate.errorMessage(
				<>
					<p>Se ha producido un error en la cancelación.</p>
					<p>Por favor, vuelva a intentarlo.</p>
				</>,
				() => setIsHidden(false)
			),
		);

	};

	const actionButtons: CTNModalProps['actionButtons'] = [
		{
			key: 'tertiary',
			mode: 'tertiary',
			label: 'Cancelar',
			size: 'size-40',
			minWidth: '140',
			onClick: onClose,
		},
		{
			key: 'primary',
			mode: 'danger',
			disabled: hasMissingRequired || loading,
			label: 'Cancelar solicitud',
			minWidth: '140',
			size: 'size-40',
			onClick() {
				handleSubmit(handleSave)();
			},
		},
	];

	useEffect(() => {
		const check = async () => {
			const hasAllRequiredFields = await peekValidation({
				schema,
				data: formData,
				fields: Object.keys(formData) as Array<keyof Form>,
			});

			setHasMissingRequired(!hasAllRequiredFields);
		};

		check();
	}, [formData, schema]);

	useEffect(() => {
		setHasMissingRequired(true);
		reset();
	}, [isOpen]);

	return (
		<CTNModal
			autoHeight
			closeable
			type="basic"
			className={classNames('c-pnp-modal', { 'u-hidden': isHidden })}
			title="Cancelar solicitud"
			size="md"
			alignButtons="right"
			actionButtons={actionButtons}
			show={isOpen}
			content={
				<>
					<CTNTextArea
						required
						name="motivo"
						label="Motivo de la cancelación"
						control={control}
						maxLength={2000}
						error={errors.motivo?.message}
						placeholder="Indique el motivo de la cancelación"
						message={`${formData?.motivo?.length}/2000`}
					/>
					<p className="u-mt-16">¿Estás seguro de cancelar la solicitud?</p>
				</>
			}
			onClose={onClose}
		/>
	);
};

export default ModalCancelacion;
